<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<script lang="ts" setup>
    import { useI18n } from 'vue-i18n';

    interface Item {
        id: number
        name: string
        icon?: string
    }

    const props = defineProps<{
        modelValue?: number | number[]
        items: Item[]
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'update:modelValue', value: number | number[]): void
        (event: 'next')
    }>();

    const { t } = useI18n();

    const selectedModel = ref(props.modelValue ?? -1);

    const handleSelection = async(id: number): Promise<void> => {
        selectedModel.value = Number(id);
        await nextTick(() => {
            emit('next');
        });
    };

    watch(() => selectedModel.value, (nVal: number | number[]) => {
        emit('update:modelValue', nVal);
    });
</script>

<template>
    <div class="event-selection">
        <div class="event-selection__content">
            <p class="event-selection__subtitle">
                {{ t('widget.titles.event-type-subtitle') }}
            </p>
            <div class="event-selection__container">
                <div class="event-selection__list">
                    <button
                        v-for="(item, index) in items"
                        :key="index"
                        :class="{
                            'event-selection__item--selected': selectedModel === item.id,
                        }"
                        class="event-selection__item"
                        type="button"
                        @click="handleSelection(item?.id)"
                    >
                        <span>
                            <template v-if="item.icon">
                                {{ `${item.icon} ` }}
                            </template>
                            {{ t(item.name) }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .event-selection {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: center;
        block-size: 100%;
        inline-size: 100%;
        padding-block-start: var(--spacing-04);

        @include min-width(md) {
            flex-direction: row;
            align-items: center;
        }

        @include min-width(lg) {
            padding-block-start: 0;
        }

        &__content,
        &__container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
        }

        &__content {
            max-block-size: 100%;
        }

        &__subtitle {
            color: var(--text-primary);
            font-family: var(--font-primary);
            font-size: var(--font-h4);
            font-weight: var(--font-weight-medium);
            line-height: var(--line-height-150);
            padding-inline: var(--spacing-05);
            text-align: center;
        }

        &__container {
            overflow: hidden auto;
            block-size: 100%;
            inline-size: 100%;
            margin-block-start: var( --spacing-04);
            max-block-size: 80dvh;
            -ms-overflow-style: none; /* IE & Edge */
            padding-inline: var(--spacing-05);
            scrollbar-width: none; /* Firefox */

            &::-webkit-scrollbar {
                display: none; /* Chrome, Safari */
            }

            @include min-width(md) {
                margin-block-start: var( --spacing-14);
            }

            @include min-width(lg) {
                overflow: hidden;
                margin-block: var(--spacing-10) var(--spacing-06);
                max-block-size: 400px;
                max-inline-size: 80%;
            }
        }

        &__list {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-02);
            inline-size: 100%;
            padding-block-end: var(--spacing-07);

            @include min-width(md) {
                flex-flow: row wrap;
                align-items: center;
                justify-content: center;
                padding-inline: var(--spacing-03);
            }
        }

        &__item {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding: calc(var(--spacing-04) - 4px);
            border: 1px solid var(--color-white);
            border-radius: 10px;
            background: rgb(255 255 255 / 0.4);
            transition: all 0.4s ease-in-out;

            &--selected {
                padding: calc(var(--spacing-05) - 2px);
                border-color: var(--color-black-space-10);
                background-color: var(--color-pure-white);

                span {
                    font-weight: var(--font-weight-semi-bold);
                }
            }

            span {
                display: flex;
                font-size: var(--font-regular);
                font-weight: var(--font-weight-light);
                gap: var(--spacing-04);
                inline-size: 100%;
            }

            &:hover {
                padding: calc(var(--spacing-05) - 2px);
                background-color: var(--color-pure-white);
            }
        }
    }
</style>
